import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DashpivotEvent, EventNotifierService, EventTypes } from '@site-mate/dashpivot-shared-library';

import { SegmentService } from 'app/segment/segment.service';

import { ActivityService } from './activity/activity.service';
import { HomePageBaseComponent } from './home-page-base.component';
import { ErrorHandler } from '../shared/service/error-handler.service';

export abstract class HomePagePeopleBaseComponent extends HomePageBaseComponent {
  unsub = new Subject<void>();
  activities = [];
  loadingActivities = true;
  activityLimit = 30;
  activityOffset = 0;
  noMoreActivities = false;

  constructor(
    protected readonly errorHandler: ErrorHandler,
    protected readonly segmentService: SegmentService,
    protected readonly activityService: ActivityService,
  ) {
    super(segmentService);
    this.activityService.onActivitiesRefreshRequired.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.reloadActivities();
    });
  }

  abstract loadActivitiesCore();

  abstract get activityLevel();

  trackLoadActivityEvent() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.ActivityFeedLoaded, { activityLevel: this.activityLevel }),
      this.segmentService,
    );
  }

  handleThumbnailClick() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.ActivityLinkClicked, {
        activityLevel: this.activityLevel,
        Context: 'Photo Thumbnail',
      }),
      this.segmentService,
    );
  }

  handleClickTemplateLink() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.ActivityLinkClicked, {
        activityLevel: this.activityLevel,
        Context: 'Sitemate Link',
      }),
      this.segmentService,
    );
  }

  handleClickFormLink() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.ActivityLinkClicked, {
        activityLevel: this.activityLevel,
        Context: 'Form Link',
      }),
      this.segmentService,
    );
  }

  reloadActivities() {
    this.noMoreActivities = false;
    this.activityOffset = 0;
    this.load(true);
  }

  load(reloadActivities: boolean) {
    this.loadingActivities = true;
    this.noMoreActivities = false;
    return this.loadActivitiesCore().subscribe(
      (activities) => {
        this.loadingActivities = false;
        if (activities.length < this.activityLimit) {
          this.noMoreActivities = true;
        }
        this.eliminateSecsAndMilliseconds(activities);
        const originalLength = activities.length;
        this.mergePhotoActivities(activities);
        if (reloadActivities) {
          this.activities = activities;
        } else {
          this.activities.push(...activities);
        }
        this.activityOffset += originalLength;
      },
      (error) => {
        this.errorHandler.handle(error);
        this.loadingActivities = false;
      },
    );
  }

  eliminateSecsAndMilliseconds(activities) {
    activities.forEach((a) => {
      const d = new Date(a.createdAt);
      d.setSeconds(0);
      d.setMilliseconds(0);
      a.createdAt = d.toISOString();
    });
  }

  mergePhotoActivities(activities) {
    for (let i = 0; i < activities.length; i++) {
      const activity = activities[i];
      if (activity.kind === 'UPLOAD_PHOTO') {
        // search forwards
        for (let j = i + 1; j < activities.length; ) {
          const nextActivity = activities[j];
          if (
            nextActivity.kind === activity.kind &&
            nextActivity.user &&
            activity.user &&
            nextActivity.user.id === activity.user.id &&
            nextActivity.createdAt === activity.createdAt
          ) {
            activity.photos.push(...nextActivity.photos);
            activities.splice(j, 1);
          } else {
            j += 1;
          }
        }
      }
    }
  }

  loadActivities() {
    this.trackLoadActivityEvent();
    this.load(false);
  }
}
